// v0.46 785 — the DOCUMENT TEMPLATE on the web documents (quote, statement, BL, the order-page invoice / proforma). The API renders the header /
// footer from the admin setting (structured JSON -> doc-* markup, every value escaped) and tells each document which optional columns /
// sections to print. Until it answers, and whenever the setting is the seeded default, the documents keep their hard-coded markup unchanged
// (legacy_header / legacy_footer). Header/footer pieces carry class doc-tpl-screen: DocumentSheet drops them from the body it posts for the
// server PDF, whose frame repeats them on every page. The server-rendered stored invoice (InvoiceSheet) applies the template server-side.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const TTL_MS = 60000;
const cache = new Map();   // `${docType}|${lang}|${no}|${date}` -> { at, p }
export function invalidateDocTemplate() { cache.clear(); }

// vars: { document_number, date } (the date already formatted per Settings > Formatting) for the {document_number} / {date} tokens; partner_id (47245) = the customer
export function useDocTemplate(docType, vars = {}) {
  const { i18n } = useTranslation();
  const lang = i18n.language && i18n.language.startsWith('fr') ? 'fr' : 'en';
  const key = `${docType}|${lang}|${vars.document_number || ''}|${vars.date || ''}|${vars.partner_id ?? ''}|${vars.quote_date || ''}|${vars.quote_validity || ''}|${vars.proforma_validity || ''}`;   // 47260: validity tokens part of the cache key
  const [tpl, setTpl] = useState(null);
  useEffect(() => {
    let alive = true;
    let c = cache.get(key);
    if (!c || Date.now() - c.at > TTL_MS) {
      c = { at: Date.now(), p: api.getDocTemplateResolved(docType, lang, vars).catch(() => { cache.delete(key); return null; }) };
      cache.set(key, c);
    }
    c.p.then(t => { if (alive) setTpl(t); });
    return () => { alive = false; };
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  return tpl;
}

export const showBrand = (tpl) => !tpl || tpl.legacy_header;
// no brand beside it: the title block keeps its right-hand place (inline, so the seeded documents' markup and collected CSS stay unchanged)
export const titleStyle = (tpl) => (showBrand(tpl) ? undefined : { marginLeft: 'auto' });
const NOTE = { fontSize: '10.5px', color: '#44524a', marginTop: 12 };
// ordered visible columns of THIS document (ids it does not draw are skipped); no template yet -> the document's own order
export const tplColumns = (tpl, ids) => (tpl ? tpl.columns.filter(c => ids.includes(c)) : ids);
export const tplSection = (tpl, k) => !tpl || !tpl.sections || tpl.sections[k] !== false;
// the sheet's data-doc-no / data-doc-date (PDF tokens) only when the template prints something new
export const tplVars = (tpl, vars) => (tpl && (!tpl.legacy_header || !tpl.legacy_footer) ? vars : undefined);

// 47245 (GES-447 S4) the DOCUMENT CONTENT HEADER: the API's body_top { hide: [field], legal: [{field, label, value}] } (absent = nothing hidden, no legal id = today's sheet).
// tplShown(tpl, field) = the body-top line prints (Mandatory lines are never in hide: the API re-checks); TplLegal = the customer legal ids under the customer, filled ones only.
export const tplShown = (tpl, field) => !(tpl && tpl.body_top && (tpl.body_top.hide || []).includes(field));
export function TplLegal({ tpl }) {
  const rows = (tpl && tpl.body_top && tpl.body_top.legal) || [];
  return rows.map((l) => <div key={l.field} className="doc-legal" data-field={l.field} style={{ whiteSpace: 'pre-line' }}>{l.label}: {l.value}</div>);
}

// render the visible columns in template order: map = { id: element | null }
export const cells = (cols, map) => cols.map(c => <React.Fragment key={c}>{map[c]}</React.Fragment>);

export function TplPart({ tpl, part }) {
  const html = tpl && tpl[`${part}_html`];
  return html ? <div className="doc-tpl-host" dangerouslySetInnerHTML={{ __html: html }} /> : null;
}

// bank details + standing terms (plain text, the setting's), then the footer piece
export function TplEnd({ tpl }) {
  if (!tpl) return null;
  const bank = tpl.sections && tpl.sections.bank_details && (tpl.bank_lines || []).length > 0;
  const terms = tplSection(tpl, 'standing_terms') && tpl.terms_text;
  return <>
    {bank && <div className="doc-tpl-bank" style={NOTE}><b>{tpl.bank_title}</b>{tpl.bank_lines.map((l, i) => <React.Fragment key={i}><br />{l}</React.Fragment>)}</div>}
    {terms && <div className="doc-tpl-terms" style={{ ...NOTE, whiteSpace: 'pre-line' }}>{tpl.terms_text}</div>}
    <TplPart tpl={tpl} part="footer" />
  </>;
}
